import type { CommentMention } from "@msc/shared";

/**
 * @-Erwähnungen im Kommentarfeld (#275) — die Regeln ohne React, damit sie
 * sich prüfen lassen: Wo tippt jemand gerade einen Namen, was setzt die
 * Auswahl ein, und welche Erwähnungen stehen beim Absenden noch im Text.
 */

/** Längster Suchtext nach dem „@" — Vor- und Nachname mit Leerzeichen passen. */
const MAX_QUERY = 40;

export interface ActiveMention {
  /** Position des „@" im Text. */
  start: number;
  /** Was nach dem „@" bis zum Cursor steht. */
  query: string;
}

/**
 * Die Erwähnung, an der der Cursor gerade steht — oder `null`. Ein „@"
 * zählt nur am Zeilenanfang oder nach Leerraum: „name@firma.de" ist eine
 * Adresse, keine Erwähnung. Zwischen „@" und Cursor darf kein Zeilenumbruch
 * liegen, und der Suchtext beginnt nicht mit Leerzeichen.
 *
 * `done` sind die Namen bereits gewählter Erwähnungen: Steht hinter dem „@"
 * einer davon, ist die Erwähnung abgeschlossen — sonst ginge die Liste
 * direkt nach der Auswahl wieder auf, weil Namen Leerzeichen enthalten
 * dürfen und „@Nina Kreuz " für sich genommen wie eine Suche aussieht.
 */
export function activeMention(text: string, caret: number, done: readonly string[] = []): ActiveMention | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  if (query.length > MAX_QUERY || /[\r\n]/.test(query) || /^\s/.test(query)) return null;
  if (done.some((name) => query.startsWith(name))) return null;
  return { start: at, query };
}

/**
 * Setzt „@Anzeigename " an die Stelle der laufenden Erwähnung und liefert den
 * neuen Text samt Cursorposition dahinter.
 */
export function insertMention(
  text: string,
  active: ActiveMention,
  caret: number,
  displayName: string,
): { text: string; caret: number } {
  const inserted = `@${displayName} `;
  const next = text.slice(0, active.start) + inserted + text.slice(caret);
  return { text: next, caret: active.start + inserted.length };
}

/**
 * Die gewählten Erwähnungen, die beim Absenden noch als „@Anzeigename" im
 * Text stehen — wer den Namen wieder gelöscht hat, wird nicht benachrichtigt.
 * Je Person höchstens einmal.
 */
export function mentionsInText(text: string, chosen: readonly CommentMention[]): CommentMention[] {
  const seen = new Set<string>();
  return chosen.filter((m) => {
    if (seen.has(m.accountId) || !text.includes(`@${m.displayName}`)) return false;
    seen.add(m.accountId);
    return true;
  });
}
